import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  listingsApi,
  type BuyerSubmissionDetailDto,
  type EscalateBuyerSubmissionPayload,
} from "@/api/buyers";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  CellDoorControl,
  Input,
  Skeleton,
  StateMark,
  StatusBadge,
  StatusStepper,
  SubTable,
  Textarea,
  useToast,
  type CellDoor,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";
import { DetailSection } from "./detailBits";
import { InlineQueryError } from "./queryStates";
import {
  computeEscalationReadiness,
  type EscalationRequirement,
} from "./submission/escalationReadiness";

/**
 * The hand-off to Deal Staging: what it needs, what is in the way, who clears
 * each thing, what would be created — and then one terminal action.
 *
 * <p>Two states, never both: a submission that has not been escalated shows the
 * readiness page; one that has shows progress READ THROUGH the pointer. The
 * submission's own status goes terminal at escalation and nothing writes deal
 * state back onto it.</p>
 *
 * <p>🔴 <b>It reads the same computation Overview does.</b>
 * {@link computeEscalationReadiness} is pure and shared, because two surfaces
 * narrating "what is blocking this" from separate logic drift — and this one
 * would drift in front of a specialist mid-call. Overview summarises and links
 * here; the doors live here, which is Layout Standard §4.1a.</p>
 *
 * <p>🔴 <b>It composes no refusal copy.</b> Every refusal is the server's, from
 * <c>BuyerSubmissionEscalationBlockedException</c>, rendered verbatim — so the
 * pre-check and the refused POST cannot say different things.</p>
 */

const SELECT_CLASS =
  "w-full rounded-sm border border-gray-mid bg-white-pure px-space-2 py-[6px] text-small text-ink";

/**
 * The fused ladder — staging, then the deal's own pipeline. One progression
 * rather than two systems a specialist has to join in their head.
 *
 * Mirrors the broker's own status ribbon so the two surfaces describe the same
 * deal in the same words; the server computes the stop with the same pure
 * mapper that ribbon uses.
 */
const RIBBON: ReadonlyArray<{ stage: string; label: string }> = [
  { stage: "Submitted", label: "Submitted" },
  { stage: "DealStaging", label: "Liaison review" },
  { stage: "Intake", label: "Intake" },
  { stage: "PreApproval", label: "Pre-approval" },
  { stage: "Underwriting", label: "Underwriting" },
  { stage: "Closing", label: "Closing" },
  { stage: "Closed", label: "Closed" },
];

export function SubmissionEscalationPanel({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  /**
   * 🔴 The page's ONE answer — the module permission AND the row's own
   * server-resolved ownership answer — never the row's bare `canEdit`.
   *
   * <p>This panel took `s.canEdit` alone until 2026-08-02, which is the same
   * asymmetry that had already been closed on its immediate neighbour, the
   * seller-prequalification panel. A reader who owned the submission but lacked
   * `Submissions.Manage` was offered "Escalate to a deal" — the single most
   * consequential action on the record, the one that mints a DealSubmission and
   * hands it to a Broker Liaison — and got a 403 on click. Offering a write you
   * will refuse is worse than hiding it: the specialist cannot tell a permission
   * problem from a broken button, and here they would be looking at a listing
   * broker waiting on the hand-off while they tried again.</p>
   *
   * <p>Required rather than optional, and rather than computed here, so that a
   * future host cannot forget it and quietly reintroduce the gap. No section of
   * this page computes its own answer.</p>
   */
  canWrite: boolean;
}) {
  if (s.escalation) return <EscalatedView s={s} />;
  return <ReadinessPage s={s} canWrite={canWrite} />;
}

// ---------------------------------------------------------------------------
// Already escalated — read through
// ---------------------------------------------------------------------------

function EscalatedView({ s }: { s: BuyerSubmissionDetailDto }) {
  const e = s.escalation!;
  const index = RIBBON.findIndex((r) => r.stage === e.stage);
  const terminal = e.stage === "Declined" || e.stage === "Withdrawn";

  return (
    <DetailSection title="Deal">
      <div className="space-y-space-4">
        {terminal ? (
          <StatusBadge tone="muted">
            {e.stage === "Declined" ? "Declined" : "Withdrawn"}
          </StatusBadge>
        ) : (
          <StatusStepper
            steps={RIBBON.map((r) => ({ key: r.stage, label: r.label }))}
            currentIndex={index < 0 ? 0 : index}
            complete={e.stage === "Closed"}
            label="Deal progress"
          />
        )}

        {/*
          Surfaced separately from the ribbon on purpose: the shared mapper folds
          "returned to you for changes" into the same stop as "your liaison is
          working it". For a specialist those are the difference between having a
          job to do and not.
        */}
        {e.needsSpecialistAttention && (
          <div className="rounded-md border border-status-warning bg-white-warm p-space-4">
            <p className="text-body font-medium">Returned for changes</p>
            <p className="text-caption text-gray-mid mt-space-1">
              The Broker Liaison sent this back with comments. Open the deal
              submission to see what they need.
            </p>
          </div>
        )}

        <dl className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
          <div>
            <dt className="text-caption text-gray-mid">Deal</dt>
            <dd className="text-body">
              {e.dealNumber && e.dealId && e.isInPipeline ? (
                <Link
                  to={`/deal-flow/deals/${e.dealId}`}
                  className="text-gold-shadow hover:underline"
                >
                  {e.dealNumber}
                </Link>
              ) : (
                /*
                  A promoted shell exists from the moment of escalation but is
                  NOT in the pipeline until a liaison imports, so linking to it
                  would send the specialist to a page the deal is not on yet.
                */
                <span className="text-gray-mid">
                  {e.dealNumber ?? "—"} · not in the pipeline yet
                </span>
              )}
            </dd>
          </div>
          {s.sellerNameAtEscalation && (
            <div>
              <dt className="text-caption text-gray-mid">Seller (at escalation)</dt>
              <dd className="text-body">{s.sellerNameAtEscalation}</dd>
            </div>
          )}
        </dl>
      </div>
    </DetailSection>
  );
}

// ---------------------------------------------------------------------------
// Not yet escalated — the readiness page
// ---------------------------------------------------------------------------

function ReadinessPage({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
}) {
  const [open, setOpen] = useState(false);

  const gate = useQuery({
    queryKey: buyerSubmissionKeys.escalation(s.id),
    queryFn: () => buyerSubmissionsApi.getEscalation(s.id),
  });

  const readiness = computeEscalationReadiness(s, gate.data);

  if (gate.isLoading) {
    return (
      <DetailSection title="Escalate to a deal">
        <Skeleton className="h-64 w-full" />
      </DetailSection>
    );
  }

  // 🔴 Was `return null` — the whole gate disappeared, action and all, so a
  // specialist with a listing broker waiting saw an empty section and no reason
  // for it. There is nothing more misleading than an absent control.
  if (gate.isError) {
    return (
      <DetailSection title="Escalate to a deal">
        <InlineQueryError
          what="the escalation checks"
          onRetry={() => void gate.refetch()}
          retrying={gate.isFetching}
        />
      </DetailSection>
    );
  }
  if (!gate.data) return null;

  const g = gate.data;

  return (
    <DetailSection title="Escalate to a deal">
      <div className="space-y-space-5">
        <header className="space-y-space-1">
          <p className="text-body font-medium m-0" data-testid="escalation-headline">
            {readiness.headline}
          </p>
          {/* Unchanged copy: who the deal would be brokered by, in the server's
              own terms, before anything else on the page.

              The test id is new. The broker's name now legitimately appears
              twice — here, and in the preview of the deal this would create —
              so a bare text match is ambiguous, and the assertions that used
              one were really only waiting for the gate to settle. */}
          <p className="text-caption text-gray-mid m-0" data-testid="escalation-broker-line">
            {g.brokerName
              ? `This deal would be brokered by ${g.brokerName}.`
              : "A deal is brokered by the listing agent."}
          </p>
        </header>

        {/*
          Rendered VERBATIM from the server. The refusal and this pre-check are
          single-sourced, so they cannot drift into saying different things — and
          each one names the record and who to ask, because a specialist reading
          it has a listing broker waiting.
        */}
        {!g.canEscalate && g.blockedReason && (
          <div className="rounded-md border border-gray-mid bg-white-warm p-space-4">
            <p className="text-body">{g.blockedReason}</p>
          </div>
        )}

        <RequirementsTable s={s} readiness={readiness} canWrite={canWrite} />

        {/*
          🔴 A WARNING, never a blocker — it does not touch the button below, and
          it must not start to. A seller prequalification depends on a third party
          Phoenix cannot compel, so the hand-off proceeds either way.

          Shown BEFORE the click rather than only in the result, because this is
          the one warning the specialist can act on from where they are standing:
          the seller prequalification panel on this same tab carries the invite.
          Rendered verbatim, server-composed, so this and the result cannot drift.
        */}
        {g.sellerPrequalificationWarning && (
          <div
            className="rounded-md border border-status-warning bg-white-warm p-space-4"
            data-testid="escalation-seller-prequal-warning"
          >
            <p className="text-body font-medium m-0">Seller prequalification</p>
            <p className="text-caption text-gray-mid mt-space-1 mb-0">
              {g.sellerPrequalificationWarning}
            </p>
          </div>
        )}

        <DealPreview s={s} brokerName={g.brokerName ?? null} />

        <TerminalAction
          canEscalate={g.canEscalate}
          canWrite={canWrite}
          onStart={() => setOpen(true)}
        />
      </div>

      <EscalateModal s={s} open={open} onClose={() => setOpen(false)} />
    </DetailSection>
  );
}

// ---------------------------------------------------------------------------
// What escalation needs
// ---------------------------------------------------------------------------

/**
 * The checklist, as a table: what is required, who clears it, where it stands.
 *
 * <p>🔴 <b>Each row's door is declared, and a row with nothing pressable gets
 * PROSE rather than a button.</b> Table and Tile Standard §8: never render a
 * control that cannot do what it appears to do. Two rows here are permanently
 * prose — the liaison is assigned by a Broker Manager on the broker record, and
 * the letter of intent arrives through the ordinary Documents flow — and the
 * temptation to give them a button is exactly what the rule exists to stop.</p>
 *
 * <p>An `unchecked` row gets no door at all, because there is nothing to do
 * about a requirement the server has not reached yet.</p>
 */
function RequirementsTable({
  s,
  readiness,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  readiness: ReturnType<typeof computeEscalationReadiness>;
  canWrite: boolean;
}) {
  // Held from the recruitment mutation's own answer rather than waited for, so
  // "it worked, here it is" is true the moment the button stops spinning.
  const [createdLeadId, setCreatedLeadId] = useState<string | null>(null);

  return (
    <div className="space-y-space-2">
      <h3 className="text-small font-semibold text-ink m-0">What escalation needs</h3>
      <SubTable
        caption="What escalation needs"
        data-testid="escalation-requirements"
        rows={readiness.requirements}
        rowKey={(r) => r.key}
        rowTestId={(r) => `escalation-requirement-${r.key}`}
        columns={[
          {
            key: "label",
            header: "Requirement",
            render: (r) => (
              <span className="text-small text-ink">
                {r.label}
                {r.severity === "advisory" && (
                  <span className="text-caption text-gray-mid"> · advisory</span>
                )}
              </span>
            ),
          },
          {
            key: "owner",
            header: "Who clears it",
            // 🔴 Every row names one. A blocker nobody owns is a dead end, and a
            // specialist staring at one has a listing broker waiting.
            render: (r) => <span className="text-caption text-gray-mid">{r.owner}</span>,
          },
          {
            key: "state",
            header: "Where it stands",
            render: (r) => (
              <RequirementState
                r={r}
                s={s}
                canWrite={canWrite}
                createdLeadId={createdLeadId}
                onLeadCreated={setCreatedLeadId}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

function RequirementState({
  r,
  s,
  canWrite,
  createdLeadId,
  onLeadCreated,
}: {
  r: EscalationRequirement;
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
  createdLeadId: string | null;
  onLeadCreated: (id: string | null) => void;
}) {
  if (r.state === "met") {
    return <StateMark present presentLabel="Done" />;
  }

  // 🔴 Not a blocker — a requirement the server's walk has not reached. Saying
  // "missing" here would send someone to ask a Broker Manager about a broker
  // that does not exist yet.
  if (r.state === "unchecked") {
    return (
      <span className="text-caption italic text-gray-mid" data-phx-state="unchecked">
        {r.severity === "advisory"
          ? "Not established either way."
          : "Not checked yet — the item above comes first."}
      </span>
    );
  }

  if (r.key === "broker") {
    return (
      <RecruitmentDoor
        s={s}
        canWrite={canWrite}
        createdLeadId={createdLeadId}
        onLeadCreated={onLeadCreated}
      />
    );
  }

  return <CellDoorControl door={doorFor(r, s)} />;
}

/**
 * The doors that are the same on every render.
 *
 * <p>All `derived`, and three of the four carry no `onClick` at all — which is
 * the honest shape. The state each names is set somewhere else, and
 * `CellDoorControl` renders a `derived` door with no handler as prose, never as
 * a button. The listing row is the one exception: opening the listing is a real
 * navigation that does exactly what its label says.</p>
 */
function doorFor(r: EscalationRequirement, s: BuyerSubmissionDetailDto): CellDoor {
  switch (r.key) {
    case "listing-agent":
      return {
        kind: "derived",
        explain: "The agent is recorded on the listing.",
        label: "Open the listing",
        // A new tab rather than a navigation. This body renders inside a record
        // drawer as often as it renders as a page, and a router navigation from
        // in there tears down the host page and the drawer the specialist is
        // standing in — the same call the listing body makes for its own
        // cross-record links. `useNavigate` is also a hook, so it would run on
        // every render of this always-mounted table, including in the test that
        // proves this body reads no router.
        onClick: () =>
          window.open(`/listings/${s.listingId}`, "_blank", "noopener"),
      };

    case "liaison":
      // 🔴 NO BUTTON, deliberately, and this is the row that most invites one.
      // Nothing on this screen can assign a liaison — it is a territory
      // configuration or a Broker Manager action on the broker record — so a
      // control here would be a button that cannot do what it says.
      return {
        kind: "derived",
        explain:
          "A Broker Manager assigns the liaison on the broker record. Usually their "
          + "territory just has no liaison on it yet.",
      };

    case "loi":
      // Document-backed, but the uploader is not on this tab: the letter arrives
      // through the ordinary Documents flow like every other file, and the form
      // below PICKS from what is already filed. Prose naming both halves, in the
      // server's own words, rather than a button that would open nothing.
      return {
        kind: "derived",
        explain:
          "Upload it to this submission's documents, then pick it on the form below.",
      };

    case "seller-prequalification":
      // 🔴 The invite lives in Seller prequalification on this tab, and that is
      // ONE shared component rather than two. A second invite control on the
      // same screen as the real one is two implementations of the same thing,
      // and a door that leads somewhere the reader then has to hunt is itself
      // the defect this vocabulary exists to prevent.
      return {
        kind: "derived",
        explain:
          "Invite the seller, or link one they already completed, from Seller "
          + "prequalification on this tab.",
      };

    default:
      return { kind: "derived", explain: "Not yet satisfied." };
  }
}

/**
 * Escalate the listing agent into broker recruitment, from here.
 *
 * <p>🔴 <b>§8.2 done properly: the button is labelled with the change it
 * actually makes.</b> This does NOT make the agent a Phoenix broker — it creates
 * a BrokerLead for a Broker Marketer to convert, which stays a deliberate second
 * act by someone else. So the label says "Escalate into broker recruitment" and
 * never "Mark as broker", and the row does not flip to Done afterwards, because
 * the requirement genuinely is not met yet.</p>
 *
 * <p>Same mutation the listing's own agent panel uses. The listing detail cache
 * is invalidated because that panel reads the agent row this writes.</p>
 *
 * <p>🔴 <b>No agent id, no button.</b> The id rides
 * <c>listing.listingAgentId</c>, and a submission whose listing summary did not
 * come back cannot offer this — so it degrades to prose rather than to a control
 * that would throw on click.</p>
 */
function RecruitmentDoor({
  s,
  canWrite,
  createdLeadId,
  onLeadCreated,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
  createdLeadId: string | null;
  onLeadCreated: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const agentId = s.listing?.listingAgentId ?? null;

  const escalate = useMutation({
    mutationFn: () => listingsApi.escalateAgent(agentId!, { reason: null }),
    onSuccess: (updated) => {
      onLeadCreated(updated.escalatedToLeadId ?? null);
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(
        "Escalated into broker recruitment. A Broker Marketer will work the lead.",
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (createdLeadId) {
    return (
      <span className="inline-flex flex-col items-start gap-space-1">
        <a
          href={`/leads/${createdLeadId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
        >
          Open the lead
          <ExternalLink size={12} strokeWidth={1.5} aria-hidden />
        </a>
        <span className="text-caption italic text-gray-mid">
          In recruitment. A Broker Marketer converts the lead.
        </span>
      </span>
    );
  }

  return (
    <CellDoorControl
      door={{
        kind: "derived",
        explain:
          "This creates a broker lead. A Broker Marketer converts it, and the "
          + "agent becomes a Phoenix broker then.",
        // Withheld rather than disabled when the id is missing or the reader
        // cannot write: prose is the honest fallback for a door that would not
        // work.
        ...(agentId && canWrite
          ? {
              label: escalate.isPending
                ? "Escalating…"
                : "Escalate into broker recruitment",
              onClick: () => {
                if (!escalate.isPending) escalate.mutate();
              },
            }
          : {}),
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// What would be created
// ---------------------------------------------------------------------------

/**
 * The Deal this would mint, before it is minted.
 *
 * <p>🔴 <b>It names what will NOT carry.</b> The legal entity name and the NAICS
 * code are deliberately absent from the composition — a listing carries a TRADE
 * name, and a trade name sitting in a legal-entity field produces a loan file
 * that is confidently incorrect and surfaces at closing. Saying so here is the
 * difference between a liaison expecting to fill two fields and a specialist
 * wondering why the deal looks half-built.</p>
 *
 * <p>Every figure is read off the record rather than recomputed, and the price
 * follows the composition's own precedence — the proposed offer, falling back to
 * the asking price at submission.</p>
 */
function DealPreview({
  s,
  brokerName,
}: {
  s: BuyerSubmissionDetailDto;
  brokerName: string | null;
}) {
  const price = s.proposedOfferPrice ?? s.askingPriceAtSubmission ?? null;
  const business = s.listing?.businessName?.trim() || null;

  const rows: Array<{ label: string; value: string | null }> = [
    { label: "Business (trade name)", value: business },
    { label: "Buyer", value: s.buyerDisplayName },
    { label: "Broker", value: brokerName },
    { label: "Purchase price", value: price != null ? formatCurrency(price) : null },
    {
      label: "Seller note",
      value: s.sellerNoteAmount != null ? formatCurrency(s.sellerNoteAmount) : null,
    },
    {
      label: "Down payment",
      value: s.downPaymentAmount != null ? formatCurrency(s.downPaymentAmount) : null,
    },
  ];

  return (
    <div className="space-y-space-2" data-testid="escalation-deal-preview">
      <h3 className="text-small font-semibold text-ink m-0">The deal this would create</h3>
      <dl className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label}>
            <dt className="text-caption text-gray-mid">{r.label}</dt>
            <dd className="text-small text-ink m-0">
              {r.value ?? <span className="text-gray-mid">Not recorded</span>}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-caption text-gray-mid m-0">
        A Broker Liaison picks this up in staging. They will add the business&apos;s
        legal entity name and NAICS code — the listing does not carry either.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The one terminal action
// ---------------------------------------------------------------------------

/**
 * One action, and it says what it costs.
 *
 * <p>🔴 <b>Nothing in Phoenix un-escalates a submission.</b>
 * <c>EscalatedDealSubmissionId</c> has exactly one writer and nothing clears it;
 * the status is absorbing, and <c>SetStatusAsync</c> refuses to move a submission
 * out of EscalatedToDeal. So if the deal is later declined or withdrawn in
 * staging, this submission stays settled here. That is a real limitation rather
 * than a scary-sounding caution, and the copy says it plainly — a specialist who
 * finds out afterwards has no move left.</p>
 */
function TerminalAction({
  canEscalate,
  canWrite,
  onStart,
}: {
  canEscalate: boolean;
  canWrite: boolean;
  onStart: () => void;
}) {
  return (
    <div className="rounded-md border border-border-light bg-white-warm p-space-4 space-y-space-2">
      <p className="text-caption text-gray-mid m-0">
        Escalating hands this submission to a Broker Liaison and settles it on the
        buyer side. There is no way back: nothing un-escalates a submission, so if
        the deal is later declined or withdrawn in staging, this stays settled here.
      </p>
      {/* 🔴 `canWrite`, not the row's bare `canEdit`. See the prop's own note:
          this is the action that mints a DealSubmission, and it was the last
          section on the page still gating on half the rule. */}
      <Button onClick={onStart} disabled={!canEscalate || !canWrite}>
        Escalate to a deal
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------

function EscalateModal({
  s,
  open,
  onClose,
}: {
  s: BuyerSubmissionDetailDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [sellerKind, setSellerKind] = useState<"Person" | "Entity">("Person");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [entityName, setEntityName] = useState("");
  const [loiDocumentId, setLoiDocumentId] = useState<string>(s.loiDocumentId ?? "");
  const [notes, setNotes] = useState("");

  // A PICKER over documents already in the submission's folder, never an
  // uploader — the LOI arrives through the ordinary Documents flow like every
  // other file.
  //
  // Buyer programme PR 2 NARROWED what this lists, deliberately. The folder used
  // to be keyed on the (buyer, LISTING) pair, so this picker could legitimately
  // offer a document filed by a SIBLING submission to the same listing. Documents
  // now live where they originate, on the submission's own root, so it offers
  // only this submission's documents. A submission still sitting in a
  // pre-reversal legacy folder keeps the old, wider list until a human resolves
  // it — the backfill moves only the pairs whose owning submission is provable.
  //
  // Already-escalated submissions are unaffected either way: escalation stamps
  // LoiDocumentId and the carry reads documents BY ID, never by re-querying this
  // folder.
  const documents = useQuery({
    queryKey: buyerSubmissionKeys.documents(s.id),
    queryFn: () => buyerSubmissionsApi.listDocuments(s.id),
    enabled: open,
  });

  const escalate = useMutation({
    mutationFn: (body: EscalateBuyerSubmissionPayload) =>
      buyerSubmissionsApi.escalate(s.id, body),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      // Warnings are the liaison's to-do list, not failures — e.g. a buyer
      // with no last name on file, which import needs and staging can fix.
      for (const w of result.warnings) toast.info(w);
      toast.success(
        result.dealNumber
          ? `Escalated as ${result.dealNumber}. ${result.brokerName} is the broker.`
          : `Escalated. ${result.brokerName} is the broker.`,
        {
          /*
            The write's answer, kept rather than printed and discarded.

            It deliberately targets STAGING, not the deal. A promoted shell
            exists from the moment of escalation but is not in the pipeline
            until a liaison imports it — the same fact `EscalatedView` renders
            as "not in the pipeline yet" — so `/deal-flow/deals/{dealId}` would
            send the specialist to a page the deal is not on. `dealSubmissionId`
            is non-nullable on the result and staging is where it actually is.
          */
          action: {
            label: "Open in staging",
            /*
              A new tab, not a navigation, and not `useNavigate` — for two
              separate reasons that happen to agree.

              This panel renders inside `SubmissionRecordDrawer` from three
              places, so navigating in place would tear down the host page and
              the drawer the specialist is standing in. That is the same call
              the listing body makes for its own cross-record links.

              And `useNavigate` is a hook, so it would run on every render of
              this always-mounted modal — including in the test that proves the
              submission body reads no router at all. That guarantee is
              file-scoped and load-bearing; a hook cannot be conditional.
            */
            onClick: () =>
              window.open(
                `/deal-flow/deal-staging/${result.dealSubmissionId}`,
                "_blank",
                "noopener",
              ),
          },
        },
      );
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit = useMemo(
    () => (sellerKind === "Entity" ? entityName.trim() : lastName.trim()).length > 0,
    [sellerKind, entityName, lastName],
  );

  return (
    <Modal open={open} onClose={onClose} title="Escalate to a deal" size="lg">
      <div className="space-y-space-4">
        <p className="text-caption text-gray-mid">
          A Broker Liaison picks this up in staging. They will add the business&apos;s
          legal entity name and NAICS code — the listing does not carry either.
        </p>

        <div className="space-y-space-1">
          <label htmlFor="seller-kind" className="block text-caption text-gray-mid">
            Seller
          </label>
          <select
            id="seller-kind"
            data-testid="escalate-seller-kind"
            className={SELECT_CLASS}
            value={sellerKind}
            onChange={(ev) =>
              setSellerKind(ev.target.value === "Entity" ? "Entity" : "Person")
            }
          >
            <option value="Person">A person</option>
            <option value="Entity">A company</option>
          </select>
        </div>

        {sellerKind === "Person" ? (
          <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
            <div className="space-y-space-1">
              <label htmlFor="seller-first" className="block text-caption text-gray-mid">
                Seller first name
              </label>
              <Input
                id="seller-first"
                value={firstName}
                onChange={(ev) => setFirstName(ev.target.value)}
              />
            </div>
            <div className="space-y-space-1">
              <label htmlFor="seller-last" className="block text-caption text-gray-mid">
                Seller last name
              </label>
              <Input
                id="seller-last"
                data-testid="escalate-seller-last"
                value={lastName}
                onChange={(ev) => setLastName(ev.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-space-1">
            <label htmlFor="seller-entity" className="block text-caption text-gray-mid">
              Seller legal entity name
            </label>
            <Input
              id="seller-entity"
              data-testid="escalate-seller-entity"
              value={entityName}
              onChange={(ev) => setEntityName(ev.target.value)}
            />
          </div>
        )}

        <div className="space-y-space-1">
          <label htmlFor="escalate-loi" className="block text-caption text-gray-mid">
            Signed letter of intent
          </label>
          {documents.isLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <select
              id="escalate-loi"
              data-testid="escalate-loi"
              className={SELECT_CLASS}
              value={loiDocumentId}
              onChange={(ev) => setLoiDocumentId(ev.target.value)}
            >
              <option value="">Select a document…</option>
              {(documents.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title || d.fileName}
                </option>
              ))}
            </select>
          )}
          {/* Deliberately no uploader: the LOI reaches the system through the
              normal Documents flow like every other file. */}
          <p className="text-caption text-gray-mid">
            Upload it through this submission&apos;s documents first, then pick it here.
          </p>
        </div>

        <div className="space-y-space-1">
          <label htmlFor="escalate-notes" className="block text-caption text-gray-mid">
            Notes for the liaison
          </label>
          <Textarea
            id="escalate-notes"
            value={notes}
            onChange={(ev) => setNotes(ev.target.value)}
          />
        </div>

        {/* 🔴 The last thing before the button, because it is the thing that
            cannot be undone afterwards. Same fact the page states above; stated
            again here because a specialist who opened this from a clear gate may
            not have read the page. */}
        <p className="text-caption text-gray-mid m-0">
          This is one-way. Nothing un-escalates a submission, so if the deal is
          later declined or withdrawn in staging, this submission stays settled.
        </p>

        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || escalate.isPending}
            onClick={() =>
              escalate.mutate({
                sellerKind,
                sellerFirstName: firstName.trim() || null,
                sellerLastName: lastName.trim() || null,
                sellerEntityName: entityName.trim() || null,
                loiDocumentId: loiDocumentId || null,
                notes: notes.trim() || null,
              })
            }
          >
            {escalate.isPending ? "Escalating…" : "Escalate"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
